/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import {
  PropsWithChildren,
  PropsWithoutRef,
  RefAttributes,
  useImperativeHandle,
} from "react";

import * as React from "react";
import styled from "styled-components";
import { useElementSize } from "hooks/useElementSize";

interface DottedBorderProps {
  height: number;
  width: number;
  color: string;
  dashlength: string | number;
  radius?: string | number;
  shouldhidedots?: string;
}

const DottedBorder = styled.div<DottedBorderProps>`
  width: 100%;
  height: 100%;
  background-image: ${(props): string =>
    props.shouldhidedots === "true"
      ? "none"
      : `url("data:image/svg+xml;base64,${window.btoa(
          `<svg xmlns='http://www.w3.org/2000/svg' width='${
            props.width
          }' height='${props.height}' viewBox='0 0 ${props.width} ${
            props.height
          }'> <g fill='none' stroke='${
            props.color
          }' stroke-linecap='round' stroke-width='3' stroke-dasharray='${
            props.dashlength
          } 8'> <rect width='${props.width}' height='${props.height}' rx='${
            props.radius ? props.radius : "16"
          }' stroke='none'/> <rect x='1.5' y='1.5' width='${
            props.width - 3
          }' height='${props.height - 3}' rx='${
            props.radius ? props.radius : "14.5"
          }' fill='none'/> </g> </svg>`
        )}")`};
  background-repeat: no-repeat;
  border: ${(props): string =>
    props.shouldhidedots === "true" ? "3px solid #EDEEFF" : "none"};
  border-radius: ${(props): string =>
    props.radius ? `${props.radius}px` : "none"};
`;

export default DottedBorder;

const AbsolutePositionedBorder = styled(DottedBorder)`
  position: absolute;
  pointer-events: none;
  top: 0;
  left: 0;
`;

export interface WithDottedBorderProps {
  color: string;
  dashlength: string | number;
  radius?: string | number;
  className?: string;
  shouldhidedots?: string;
  borderStyle?: React.CSSProperties;
}

export const WithDottedBorder: React.ForwardRefExoticComponent<
  PropsWithoutRef<PropsWithChildren<WithDottedBorderProps>> &
    RefAttributes<React.RefObject<HTMLDivElement>>
> = React.forwardRef<React.RefObject<HTMLDivElement>, WithDottedBorderProps>(
  ({ children, className, color, borderStyle, ...props }, externalRef) => {
    const { ref, width, height } = useElementSize<HTMLDivElement>();

    useImperativeHandle(externalRef, (): React.RefObject<HTMLDivElement> => {
      return ref;
    });

    return (
      <div
        className={className}
        ref={ref}
        style={{ position: "relative" }}
        data-testid="dotted-border"
        {...props}
      >
        {children}
        <AbsolutePositionedBorder
          className="div-implementing-absolute-positioned-dotted-border"
          color={color}
          width={width}
          height={height}
          dashlength={props.dashlength}
          radius={props.radius}
          shouldhidedots={props.shouldhidedots}
          style={borderStyle}
        />
      </div>
    );
  }
);
